import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdOutlineArrowBack } from 'react-icons/md';
import axios from 'config/axios';
import { useAppSelector } from 'hooks/useStore';
import Loader from 'components/Loader';
import FillAppSubtab from 'components/FillAppSubtab';
import { useFillAppSchema } from 'hooks/useFillAppSchema';
import { useFillAppAutosave, type AutosaveStatus } from 'hooks/useFillAppAutosave';
import { useFillAppColleges } from 'hooks/useFillAppColleges';
import type { College, CollegeChoice } from 'global/colleges';
import { FillAppCommentsContext, type StudentFillAppCommentsCtx } from 'components/FillAppField/commentsContext';
import {
    type FillAppSchema,
    type FillAppScreen,
    type FillAppSubtab as SubtabDef,
    type FillAppValues,
    type FillAppFieldComment,
    type SectionStatus,
    buildQidLocationMap,
    computeOverallProgress,
    computeScreenProgress,
    computeSubtabFilled,
    screenRequiredComplete,
    subtabIsVisible,
    subtabRequiredComplete,
    visibleQidsOfSubtab,
} from 'global/fillapp';

import './index.css';

interface FillAppResponse {
    id: number;
    isEnabled: boolean;
    cycleYear: number;
    values: FillAppValues;
    sectionStatus: SectionStatus;
    colleges: CollegeChoice[];
    fieldComments: FillAppFieldComment[];
    lastSavedAt: string | null;
}

// Comments whose field is currently hidden by conditional answers or inactive
// repeating-group slots — surfaced above the form so they can't be lost.
function HiddenFieldComments({ subtab, values, comments, onResolve }: {
    subtab: SubtabDef;
    values: FillAppValues;
    comments: FillAppFieldComment[];
    onResolve: (commentId: number) => Promise<void>;
}) {
    const subtabQids = useMemo(() => new Set(subtab.fields.map(f => f.qid)), [subtab]);
    const visible = useMemo(() => visibleQidsOfSubtab(subtab, values), [subtab, values]);
    const hidden = comments.filter(c => subtabQids.has(c.qid) && !visible.has(c.qid) && !c.isResolved);
    if (hidden.length === 0) return null;
    const labelOf = (qid: string) =>
        (subtab.fields.find(f => f.qid === qid)?.label || qid).replace(/<[^>]+>/g, '');
    return (
        <div className='fappSection__hiddenComments'>
            <b>{hidden.length} tutor comment{hidden.length > 1 ? 's' : ''} on fields not currently shown</b>
            {hidden.map(c => (
                <div key={c.id} className='fappSection__hiddenComment'>
                    <span><i>{labelOf(c.qid)}</i> — {c.body}</span>
                    <button type='button' onClick={() => onResolve(c.id).catch(() => undefined)}>Mark resolved</button>
                </div>
            ))}
        </div>
    );
}

// Mirrors real Common App: the Courses & Grades section lists which of the
// student's chosen colleges require the self-reported courses and which
// don't use them.
function CoursesGradesColleges({ choices, cycleYear }: {
    choices: CollegeChoice[];
    cycleYear: number;
}) {
    const navigate = useNavigate();
    const { grid } = useFillAppColleges(cycleYear);
    if (!grid) return null;
    if (choices.length === 0) {
        return (
            <div className='fappSection__cgColleges fappSection__cgColleges--empty'>
                Add colleges to <button type='button' onClick={() => navigate('/college-apps?tab=colleges')}>My Colleges</button> to
                see which of them require Courses &amp; Grades.
            </div>
        );
    }
    const byId = new Map(grid.colleges.map(c => [c.id, c]));
    const selected = choices.map(ch => byId.get(ch.id)).filter(Boolean) as College[];
    const required = selected.filter(c => c.coursesGrades).map(c => c.name);
    const notUsed = selected.filter(c => !c.coursesGrades).map(c => c.name);
    return (
        <div className='fappSection__cgColleges'>
            <div className='fappSection__cgRow'>
                <span className='fappSection__cgTag fappSection__cgTag--req'>
                    Required by {required.length}
                </span>
                <span className='fappSection__cgNames'>
                    {required.length > 0 ? required.join(', ') : 'None of your colleges'}
                </span>
            </div>
            <div className='fappSection__cgRow'>
                <span className='fappSection__cgTag'>
                    Not used by {notUsed.length}
                </span>
                <span className='fappSection__cgNames'>
                    {notUsed.length > 0 ? notUsed.join(', ') : 'None of your colleges'}
                </span>
            </div>
        </div>
    );
}

type SubtabStatus = 'complete' | 'in-progress' | 'empty' | 'active';

const STATUS_SYMBOL: Record<SubtabStatus, string> = {
    'complete': '✓',
    'in-progress': '◐',
    'empty': '○',
    'active': '●',
};

function subtabStatusOf(subtab: SubtabDef, values: FillAppValues): SubtabStatus {
    if (subtabRequiredComplete(subtab, values)) return 'complete';
    const { filled } = computeSubtabFilled(subtab, values);
    return filled === 0 ? 'empty' : 'in-progress';
}

function formatRelative(iso: string | null, now: number): string {
    if (!iso) return '';
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return '';
    const seconds = Math.max(1, Math.round((now - t) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
}

function SaveIndicator({ status, lastSavedAt }: { status: AutosaveStatus; lastSavedAt: string | null }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    let body: React.ReactNode = null;
    let modifier = '';
    if (status === 'saving') { body = 'Saving…'; modifier = 'fappSection__save--saving'; }
    else if (status === 'conflict') { body = 'Conflict — refresh'; modifier = 'fappSection__save--conflict'; }
    else if (status === 'error') { body = 'Error — retrying'; modifier = 'fappSection__save--error'; }
    else body = lastSavedAt ? `Saved ${formatRelative(lastSavedAt, now)}` : 'Not yet saved';
    return <div className={`fappSection__save ${modifier}`}>{body}</div>;
}

function findLocated(schema: FillAppSchema, screenId: string, subtabId: string) {
    const screen = schema.screens.find(s => s.id === screenId);
    if (!screen) return null;
    const subtabIndex = screen.subtabs.findIndex(st => st.id === subtabId);
    if (subtabIndex < 0) return null;
    return { screen, subtab: screen.subtabs[subtabIndex], subtabIndex };
}

function FillAppSection() {
    const { screenId, subtabId } = useParams<{ screenId: string; subtabId: string }>();
    const navigate = useNavigate();
    const { organization } = useAppSelector(state => state.organization);

    const [fillApp, setFillApp] = useState<FillAppResponse | null>(null);
    // Follow the student's own cycle once the payload lands; the hook's
    // default covers the first render, and refetches when it changes.
    const { schema, loading: schemaLoading, error: schemaError } =
        useFillAppSchema(fillApp?.cycleYear);
    const [loadError, setLoadError] = useState<string | null>(null);
    // Tutor field comments — kept separate from the autosave hook.
    const [comments, setComments] = useState<FillAppFieldComment[]>([]);

    useEffect(() => {
        if (!organization.urlSlug) return;
        let cancelled = false;
        axios.get(`fillapp/student/${organization.urlSlug}/`)
            .then(res => {
                if (cancelled) return;
                if (!res.data.fillApp) {
                    setFillApp(null);
                    setLoadError('College Apps is not enabled for your account.');
                    return;
                }
                setFillApp(res.data.fillApp);
                // Already-resolved notes are hidden on load — a note the student
                // resolves DURING this visit stays visible (marked resolved) so the
                // resolution feels acknowledged, then disappears on the next visit.
                setComments((res.data.fillApp.fieldComments || [])
                    .filter((c: FillAppFieldComment) => !c.isResolved));
            })
            .catch(err => {
                if (cancelled) return;
                setLoadError(err?.response?.data?.message || 'Failed to load College Apps.');
            });
        return () => { cancelled = true; };
    }, [organization.urlSlug]);

    // Optimistic resolve with rollback; a 400 means the tutor deleted the
    // comment meanwhile — drop it locally.
    const resolveComment = React.useCallback((commentId: number) => {
        let prev: FillAppFieldComment[] = [];
        setComments(cur => {
            prev = cur;
            return cur.map(c => (c.id === commentId
                ? { ...c, isResolved: true, resolvedAt: new Date().toISOString() }
                : c));
        });
        return axios.post(`fillapp/student/fieldcomment/${commentId}/resolve/`)
            .then(res => {
                setComments(cur => cur.map(c => (c.id === commentId ? res.data.comment : c)));
            })
            .catch(err => {
                if (err?.response?.status === 400) {
                    setComments(cur => cur.filter(c => c.id !== commentId));
                } else {
                    setComments(prev);
                }
                throw err;
            });
    }, []);

    const commentsCtx = useMemo<StudentFillAppCommentsCtx>(() => {
        const byQid: Record<string, FillAppFieldComment[]> = {};
        for (const c of comments) (byQid[c.qid] = byQid[c.qid] || []).push(c);
        return { commentsByQid: byQid, resolveComment };
    }, [comments, resolveComment]);

    const qidLocation = useMemo(() => (schema ? buildQidLocationMap(schema) : null), [schema]);
    const openCounts = useMemo(() => {
        const bySubtab: Record<string, number> = {};
        const byScreen: Record<string, number> = {};
        const byQid: Record<string, number> = {};
        if (!qidLocation) return { bySubtab, byScreen, byQid };
        for (const c of comments) {
            if (c.isResolved) continue;
            const loc = qidLocation.get(c.qid);
            if (!loc) continue;
            bySubtab[loc.subtabId] = (bySubtab[loc.subtabId] || 0) + 1;
            byScreen[loc.screenId] = (byScreen[loc.screenId] || 0) + 1;
            byQid[c.qid] = (byQid[c.qid] || 0) + 1;
        }
        return { bySubtab, byScreen, byQid };
    }, [comments, qidLocation]);

    // "Review the first note →" — expands the flagged card (navigating first
    // if the note lives in another section). tick re-fires for repeat clicks.
    const [focus, setFocus] = useState<{ qid: string; tick: number } | null>(null);

    const autosave = useFillAppAutosave({
        studentFillAppId: fillApp?.id ?? null,
        initialValues: fillApp?.values || {},
        initialLastSavedAt: fillApp?.lastSavedAt ?? null,
    });

    useEffect(() => {
        if (!fillApp) return;
        autosave.resetWithServerState({
            values: fillApp.values || {},
            lastSavedAt: fillApp.lastSavedAt ?? null,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fillApp?.id]);

    const located = useMemo(() => {
        if (!schema || !screenId || !subtabId) return null;
        return findLocated(schema, screenId, subtabId);
    }, [schema, screenId, subtabId]);

    if (schemaLoading || !schema || (!fillApp && !loadError)) return <Loader />;
    if (schemaError) return <div className='layout'><div className='fappSection__error'>{schemaError}</div></div>;
    if (loadError) return <div className='layout'><div className='fappSection__error'>{loadError}</div></div>;
    if (!located) {
        return (
            <div className='layout'>
                <div className='fappSection__error'>That College Apps section doesn't exist.</div>
            </div>
        );
    }

    const { screen, subtab } = located;

    // Sections gated by household answers (step-parents, legal guardian) are
    // hidden from navigation entirely, like the real Common App.
    const visibleSubtabsOf = (s: FillAppScreen) =>
        s.subtabs.filter(st => subtabIsVisible(st, autosave.values));

    const visibleSubtabs = visibleSubtabsOf(screen);
    const visibleIndex = visibleSubtabs.findIndex(st => st.id === subtab.id);
    const nextSubtab = visibleIndex >= 0 ? visibleSubtabs[visibleIndex + 1] ?? null : null;

    const switchScreen = (targetScreen: FillAppScreen) => {
        autosave.flushNow();
        const firstSubtabId = visibleSubtabsOf(targetScreen)[0]?.id;
        if (firstSubtabId) navigate(`/college-apps/${targetScreen.id}/${firstSubtabId}`);
    };

    const switchSubtab = (targetScreenId: string, targetSubtabId: string) => {
        autosave.flushNow();
        navigate(`/college-apps/${targetScreenId}/${targetSubtabId}`);
    };

    const goToNext = () => {
        autosave.flushNow();
        if (nextSubtab) {
            navigate(`/college-apps/${screen.id}/${nextSubtab.id}`);
            return;
        }
        // Last subtab in this screen — jump to first subtab of next screen, or
        // back to dashboard if this was the last screen overall.
        const screenIdx = schema.screens.findIndex(s => s.id === screen.id);
        const nextScreen = screenIdx >= 0 ? schema.screens[screenIdx + 1] : null;
        if (nextScreen) {
            const firstSubtabId = visibleSubtabsOf(nextScreen)[0]?.id;
            if (firstSubtabId) {
                navigate(`/college-apps/${nextScreen.id}/${firstSubtabId}`);
                return;
            }
        }
        navigate('/college-apps');
    };

    const handleRefresh = () => window.location.reload();

    const prevSubtab = visibleIndex > 0 ? visibleSubtabs[visibleIndex - 1] : null;
    const goBack = () => {
        autosave.flushNow();
        if (prevSubtab) navigate(`/college-apps/${screen.id}/${prevSubtab.id}`);
        else navigate('/college-apps');
    };

    // Tutor-feedback wayfinding: totals + per-screen breakdown for the banner,
    // and the jump to the first (schema-ordered) unresolved note.
    const openTotal = comments.filter(c => !c.isResolved).length;
    const openBreakdown = schema.screens
        .filter(s => (openCounts.byScreen[s.id] || 0) > 0)
        .map(s => `${openCounts.byScreen[s.id]} in ${s.label}`)
        .join(', ');
    const reviewFirstNote = () => {
        if (!qidLocation) return;
        const order = new Map<string, number>();
        let i = 0;
        for (const sc of schema.screens) for (const st of sc.subtabs) for (const f of st.fields) order.set(f.qid, i++);
        const target = comments
            .filter(c => !c.isResolved)
            .sort((a, b) => (order.get(a.qid) ?? Infinity) - (order.get(b.qid) ?? Infinity))[0];
        if (!target) return;
        const loc = qidLocation.get(target.qid);
        if (loc && (loc.screenId !== screen.id || loc.subtabId !== subtab.id)) {
            autosave.flushNow();
            navigate(`/college-apps/${loc.screenId}/${loc.subtabId}`);
        }
        setFocus({ qid: target.qid, tick: Date.now() });
    };

    const overall = computeOverallProgress(schema, autosave.values);

    return (
        <div className='fappSection layout'>
            <Helmet>
                <title>{subtab.title || subtab.label} - College Apps - {organization.name}</title>
            </Helmet>

            <button
                type='button'
                className='pageBack'
                onClick={() => { autosave.flushNow(); navigate('/college-apps'); }}
            >
                <MdOutlineArrowBack />
                Back to dashboard
            </button>

            <div className='fappSection__columns'>
                <aside className='fappSection__sidebar'>
                    <div className='fappSection__sidebarHead'>
                        <div className='fappSection__sidebarHeadRow'>
                            <h3 className='fappSection__sidebarTitle'>Your application</h3>
                            <span className='fappSection__overallPct'>{overall.percent}%</span>
                        </div>
                        <div className='fappSection__overallBar'>
                            <span style={{ width: `${overall.percent}%` }} />
                        </div>
                    </div>
                    {schema.screens.map(s => {
                        const expanded = s.id === screen.id;
                        const { percent } = computeScreenProgress(s, autosave.values);
                        const complete = screenRequiredComplete(s, autosave.values);
                        return (
                            <React.Fragment key={s.id}>
                                <div
                                    className={`fappSection__screenItem ${expanded ? 'fappSection__screenItem--expanded' : ''}`}
                                    onClick={() => { if (!expanded) switchScreen(s); }}
                                >
                                    <svg className='fappSection__screenChevron' viewBox='0 0 24 24' fill='currentColor'>
                                        <path d='M9.4 6L8 7.4l4.6 4.6L8 16.6 9.4 18l6-6z' />
                                    </svg>
                                    <div className='fappSection__screenInfo'>
                                        <span className='fappSection__screenLabel'>{s.label}</span>
                                        {(openCounts.byScreen[s.id] || 0) > 0 && (
                                            <span className='fappSection__commentBadge'>{openCounts.byScreen[s.id]}</span>
                                        )}
                                        {complete
                                            ? <span className='fappSection__screenDone'>✓</span>
                                            : <span className='fappSection__screenPct'>{percent}%</span>}
                                    </div>
                                </div>
                                {expanded && (
                                    <div className='fappSection__subtabList'>
                                        {visibleSubtabsOf(s).map(st => {
                                            const isActive = st.id === subtab.id;
                                            const status: SubtabStatus = isActive
                                                ? 'active'
                                                : subtabStatusOf(st, autosave.values);
                                            return (
                                                <div
                                                    key={st.id}
                                                    className={`fappSection__subtabNav ${isActive ? 'fappSection__subtabNav--active' : ''}`}
                                                    onClick={() => switchSubtab(s.id, st.id)}
                                                >
                                                    <span className={`fappSection__subtabStatus fappSection__subtabStatus--${status}`}>
                                                        {STATUS_SYMBOL[status]}
                                                    </span>
                                                    <span className='fappSection__subtabLabel'>{st.title || st.label}</span>
                                                    {(openCounts.bySubtab[st.id] || 0) > 0 && (
                                                        <span className='fappSection__commentBadge'>{openCounts.bySubtab[st.id]}</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </aside>

                <main className='fappSection__main'>
                    <div className='fappSection__eyebrow'>{screen.label}</div>
                    <div className='fappSection__headerRow'>
                        <h1 className='fappSection__heading'>{subtab.title || subtab.label}</h1>
                        <SaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt} />
                    </div>

                    {openTotal > 0 && (
                        <div className='fappSection__tutorBanner'>
                            <span className='fappSection__tutorBannerText'>
                                Your tutor left {openTotal} note{openTotal === 1 ? '' : 's'}
                                {openBreakdown ? ` — ${openBreakdown}.` : '.'}
                            </span>
                            <button type='button' className='fappSection__tutorBannerBtn' onClick={reviewFirstNote}>
                                Review the first note →
                            </button>
                        </div>
                    )}

                    {autosave.status === 'conflict' && (
                        <div className='fappSection__conflictBanner'>
                            This form was updated elsewhere.
                            <button type='button' className='fappSection__refreshBtn' onClick={handleRefresh}>
                                Refresh
                            </button>
                        </div>
                    )}

                    {screen.id === 'courses-grades' && fillApp && (
                        <CoursesGradesColleges
                            choices={fillApp.colleges || []}
                            cycleYear={fillApp.cycleYear}
                        />
                    )}

                    <HiddenFieldComments
                        subtab={subtab}
                        values={autosave.values}
                        comments={comments}
                        onResolve={resolveComment}
                    />

                    <FillAppCommentsContext.Provider value={commentsCtx}>
                        <FillAppSubtab
                            subtab={subtab}
                            values={autosave.values}
                            cycleYear={fillApp.cycleYear}
                            choiceGroups={schema.choiceGroups}
                            onChange={autosave.setValue}
                            onBlur={autosave.flushNow}
                            openCommentsByQid={openCounts.byQid}
                            focus={focus}
                        />
                    </FillAppCommentsContext.Provider>

                    <div className='fappSection__footer'>
                        <button type='button' className='fappSection__backBtn' onClick={goBack}>
                            ← {prevSubtab ? prevSubtab.label : 'Dashboard'}
                        </button>
                        <div className='fappSection__footerNote'>All changes saved automatically.</div>
                        <button
                            type='button'
                            className='fappSection__nextBtn'
                            onClick={goToNext}
                        >
                            {nextSubtab ? `Continue to ${nextSubtab.label} →` : 'Continue →'}
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
}

export default FillAppSection;
